/**
 * TicketCard - List item card for tickets
 */

import { Card } from '@/components/ui/card';
import { Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { MessageSquare } from 'lucide-react';
import type { SupportTicket } from '../types';
import { TicketPriorityBadge, TicketStatusBadge } from './TicketStatusBadge';

interface TicketCardProps {
  ticket: SupportTicket;
  onClick?: () => void;
  className?: string;
}

export function TicketCard({ ticket, onClick, className }: TicketCardProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return 'Today';
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <Card
      className={cn(
        'p-4 cursor-pointer',
        'hover:shadow-md hover:border-primary/20',
        semanticTokens.motion.standard,
        className
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <TicketStatusBadge status={ticket.status} />
            {ticket.priority !== 'normal' && (
              <TicketPriorityBadge priority={ticket.priority} />
            )}
          </div>

          <Text className="font-medium truncate mb-1">
            {ticket.subject}
          </Text>

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{formatDate(ticket.created_at)}</span>

            {ticket.reply_count !== undefined && ticket.reply_count > 0 && (
              <span className="flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                {ticket.reply_count}
              </span>
            )}

            {ticket.unread_count !== undefined && ticket.unread_count > 0 && (
              <span className={cn(
                'px-1.5 py-0.5 rounded-full font-semibold',
                semanticTokens.typography.captionMicro,
                'bg-primary text-primary-foreground'
              )}>
                {ticket.unread_count} new
              </span>
            )}
          </div>
        </div>

        <SFIcon icon={sfIcons.chevronRight} className="h-5 w-5 text-muted-foreground flex-shrink-0" />
      </div>
    </Card>
  );
}
